import React from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { MessageCircleQuestion } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { whatsappLink } from "@/config";

const FAQS = [
    {
        q: "Dove operate?",
        a: "Siamo nati e cresciuti a Roma: conosciamo la città, il mercato turistico e le sue dinamiche a fondo. Operiamo su tutta l'area metropolitana romana.",
    },
    {
        q: "E se il mio immobile è intestato a una società?",
        a: "Nessun problema. La struttura fiscale è semplicemente diversa: la tassazione può variare e, in molti casi, risultare persino più vantaggiosa rispetto a un immobile intestato a persona fisica. Ti guidiamo noi in ogni passaggio.",
    },
    {
        q: "Le pratiche di avvio attività sono un incubo, a chi devo rivolgermi?",
        a: "È tutto vero, possono essere davvero laboriose. Ma niente paura: i nostri consulenti specializzati si occupano di CIR/CIN, SCIA e comunicazioni alla questura, garantendo una gestione a prova di legge, senza stress per te.",
    },
    {
        q: "Ho una seconda proprietà ma sono ancora indeciso sul da farsi.",
        a: "Del tutto normale, siamo qui anche per questo. Gli affitti brevi non sono sempre la scelta migliore: durante la consulenza iniziale analizziamo l'immobile insieme e ti diamo indicazioni concrete sulla strategia più adatta al tuo caso.",
    },
    {
        q: "Ho già ricevuto proposte da altri property manager, perché dovrei scegliere FaMa Apartments?",
        a: "Il mercato turistico romano è competitivo e ci sono ottimi professionisti. Ciò che distingue FaMa Apartments è la gestione diretta e sul campo di ogni aspetto: dalla cura degli ospiti a quella dell'immobile, fino alle pulizie e alla biancheria internalizzate con FaMa Lab. Un processo ottimizzato che si traduce in soggiorni memorabili per gli ospiti e margini più solidi per i proprietari.",
    },
];

export default function FAQ() {
    return (
        <section
            id="faq"
            className="relative scroll-mt-24 bg-[var(--fama-surface)] py-24 lg:py-32"
            data-testid="faq-section"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20">
                {/* Left: intro */}
                <div className="lg:col-span-5">
                    <FadeUp>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--fama-gold-soft)] text-[var(--fama-gold)]">
                                <MessageCircleQuestion size={20} strokeWidth={1.6} />
                            </span>
                            <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                                FAQ
                            </span>
                        </div>
                    </FadeUp>
                    <MaskedLines
                        as="h2"
                        lines={["Domande frequenti,", "risposte chiare."]}
                        className="mt-6 font-serif-display text-4xl leading-[1.02] tracking-[-0.01em] text-[var(--fama-ink)] sm:text-5xl lg:text-6xl"
                    />
                    <FadeUp delay={0.1}>
                        <p className="mt-7 max-w-md font-body text-base leading-relaxed text-[var(--fama-ink-soft)] sm:text-lg">
                            Quello che i proprietari ci chiedono più spesso.
                            Non trovi la tua domanda? Scrivici su WhatsApp,
                            rispondiamo di persona.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.18}>
                        <a
                            href={whatsappLink(
                                "Ciao FaMa Apartments! Ho una domanda sulla gestione del mio immobile.",
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="faq-whatsapp-button"
                            className="mt-8 inline-flex items-center gap-3 rounded-full border border-[var(--fama-ink)] px-6 py-3 font-body text-sm font-semibold text-[var(--fama-ink)] transition-colors hover:bg-[var(--fama-ink)] hover:text-[#fdfbf7]"
                        >
                            <WhatsAppIcon size={17} /> Fai la tua domanda
                        </a>
                    </FadeUp>
                </div>

                {/* Right: accordion */}
                <div className="lg:col-span-7">
                    <FadeUp>
                        <Accordion
                            type="single"
                            collapsible
                            defaultValue="item-0"
                            className="w-full"
                            data-testid="faq-accordion"
                        >
                            {FAQS.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    data-testid={`faq-item-${i}`}
                                    className="border-b border-[var(--fama-border)]"
                                >
                                    <AccordionTrigger className="group py-6 text-left font-serif-display text-xl leading-snug text-[var(--fama-ink)] hover:no-underline sm:text-2xl [&[data-state=open]]:text-[var(--fama-gold)]">
                                        <span className="mr-6 font-body text-xs font-semibold tracking-[0.18em] text-[var(--fama-gold)]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="flex-1">{item.q}</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-6 pl-[3.6rem] pr-2 font-body text-base leading-relaxed text-[var(--fama-ink-soft)]">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
